import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

import GrantHeader from "@/pages/grant-detail/GrantHeader";
import GrantSynopsis from "@/pages/grant-detail/GrantSynopsis";
import GenerateProposalSection from "@/pages/grant-detail/GenerateProposalSection";

function htmlToPlain(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

export default function GrantDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [grant, setGrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [language, setLanguage] = useState(() => {
    try {
      const raw = (navigator.language || "en").toLowerCase().split("-")[0];
      return ["en","es","fr","de","pt","it","nl","ar","zh","ja"].includes(raw) ? raw : "en";
    } catch { return "en"; }
  });

  useEffect(() => {
    api.get(`/grants/${id}`)
      .then((r) => setGrant(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const opp = grant?.opportunity || grant;
  const synopsis = opp?.synopsis || {};
  const description = synopsis.synopsisDesc || opp?.description || "";
  const agency = opp?.agencyName || opp?.topAgencyDetails?.agencyName || synopsis.agencyName || "—";
  const closeDate = synopsis.responseDate || opp?.closeDate;
  const openDate = synopsis.postingDate || opp?.openDate;
  const opNumber = opp?.opportunityNumber || opp?.number || id;
  const title = opp?.opportunityTitle || opp?.title || "Grant Opportunity";
  const contactEmail = synopsis.agencyContactEmail || opp?.agencyContactEmail || null;
  const contactName = synopsis.agencyContactName || opp?.agencyContactName || null;

  const safeDescriptionHtml = useMemo(
    () => DOMPurify.sanitize(description, {
      ALLOWED_TAGS: ["p", "br", "ul", "ol", "li", "b", "strong", "i", "em", "a", "h2", "h3", "h4"],
      ALLOWED_ATTR: ["href", "target", "rel"],
    }),
    [description],
  );

  const generate = async () => {
    if (!topic.trim()) {
      setErr("Please describe your project topic first.");
      return;
    }
    setErr("");
    setGenerating(true);
    try {
      // Step 1: queue the job. Returns 202 + {job_id, status:"pending"}.
      const { data: accepted } = await api.post("/proposals/generate", {
        grant_id: String(id),
        grant_number: String(opNumber),
        grant_title: title,
        grant_agency: agency,
        grant_description: htmlToPlain(description).slice(0, 4000),
        topic,
        agency_contact_email: contactEmail,
        agency_contact_name: contactName,
        language,
      });
      // Step 2: poll the job until it completes or fails. Sub-second response
      // from the queue endpoint sidesteps any ingress request-timeout limit.
      const jobId = accepted.job_id;
      const startedAt = Date.now();
      const POLL_INTERVAL_MS = 1500;
      const MAX_WAIT_MS = 420_000; // 7 min cap — matches the backend job watchdog
      let consecutivePollErrors = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (Date.now() - startedAt > MAX_WAIT_MS) {
          throw new Error("Generation is taking longer than expected. Please try again.");
        }
        // Transient ingress 5xx must NOT kill the flow — the background job
        // keeps running on the server. Keep polling, give up only if errors
        // persist for ~15s straight or we exceed MAX_WAIT_MS.
        let job;
        try {
          const resp = await api.get(`/proposals/jobs/${jobId}`);
          job = resp.data;
          consecutivePollErrors = 0;
        } catch (pollErr) {
          consecutivePollErrors += 1;
          if (consecutivePollErrors >= 10) {
            throw pollErr;
          }
          continue;
        }
        if (job.status === "complete" && job.proposal) {
          navigate(`/proposals/${job.proposal.id}`);
          return;
        }
        if (job.status === "failed") {
          throw new Error(job.error_detail || "Generation failed");
        }
        // status === "pending" → keep polling.
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="gf-overline">Loading grant…</div>;
  if (err && !grant) {
    return <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-4">{String(err)}</div>;
  }

  return (
    <div className="max-w-5xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900 uppercase tracking-wide"
        data-testid="grant-back"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="mt-6 border border-zinc-200 bg-white p-6 md:p-8" data-testid="grant-detail">
        <GrantHeader
          title={title} agency={agency} opNumber={opNumber}
          status={opp?.oppStatus} openDate={openDate} closeDate={closeDate}
        />
        <GrantSynopsis safeDescriptionHtml={safeDescriptionHtml} />
      </div>

      <GenerateProposalSection
        user={user}
        topic={topic} setTopic={setTopic}
        language={language} setLanguage={setLanguage}
        onGenerate={generate} generating={generating}
        errMessage={err}
      />
    </div>
  );
}
